import { useEffect, useState, ChangeEvent, useRef, MutableRefObject, RefObject } from 'react'
import { DSAudioChartProps } from 'shared-definitions/types'
import { useInView } from 'react-intersection-observer'
import { useAutoFocus } from 'shared-components/hooks/use-auto-focus'
import { CHART_EMBED_LEAD_MARGIN } from 'shared-code/chart-embed'
import {
  getVileModelMeasures,
  getVileModels,
  VileModel,
  VileModelMeasure,
  VileModelResponse,
} from '../function-services/audio-chart-api-calls'
/**
 * Interface representing the return state of the useAudioChart hook.
 */
interface NCAudioChartState {
  vileModels: VileModel[]
  filteredModels: VileModel[]
  isLoading: boolean
  selectedModel: {
    name: string | undefined
    id: number | undefined
  }
  filteredModelMeasures: VileModelMeasure[]

  selectedModelMeasure: VileModelMeasure | undefined
  scrollPosition: number | null
  dropdownOpen: boolean
  filterValue: string | undefined
  chartUrl: string | undefined
  dropdownRef: MutableRefObject<HTMLDivElement | null>
  dropdownFilterRef: RefObject<HTMLInputElement>
  vileModelMeasures: VileModelMeasure[]

  ref: (node?: Element | null | undefined) => void
  filterModels: (event: ChangeEvent<HTMLInputElement>) => void
  handleComparisonChange: (singleModel: VileModel) => Promise<void>
  handleModelMeasureChange: (singleMeasure: VileModelMeasure) => void
  updateLoadingState: (state: boolean) => void
  updateDropdownVisibility: (state: boolean) => void
  resetFilter: () => void
}

/**
 * Custom hook to manage the state and logic for the Audio Chart component.
 * It handles fetching models, measurements, generating the chart URL, and managing UI state like dropdowns and filters.
 */
export function useAudioChart(chart: DSAudioChartProps): NCAudioChartState {
  const {
    style,
    modelID,
    measureID,
    comparisonModelID: defaultComparisonModelId,
    comparisonMeasurementID: defaultComparisonMeasurementId,
    targetID,
    nudge,
    limitComparisonModels,
    allowedComparisonModelsIds,
    api_url,
  } = chart

  // --- State and Refs ---
  // Store all available models and measurements from the API
  const [vileModels, setVileModels] = useState<VileModel[]>([])
  const [vileModelMeasures, setVileModelMeasures] = useState<VileModelMeasure[]>([])

  // Store filtered lists for use in search results
  const [filteredModelMeasures, setFilteredModelMeasures] = useState<VileModelMeasure[]>([])
  const [filteredModels, setFilteredModels] = useState<VileModel[]>([])

  // UI state management
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [selectedModel, setSelectedModel] = useState<{
    name: string | undefined
    id: number | undefined
  }>({ name: undefined, id: undefined })
  const [selectedModelMeasure, setSelectedModelMeasure] = useState<VileModelMeasure>()

  // Chart configuration state
  const [chartUrl, setChartUrl] = useState<string>()
  const [scrollPosition, setScrollPosition] = useState<number | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [filterValue, setFilterValue] = useState<string>('')

  // DOM references
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const dropdownFilterRef = useAutoFocus<HTMLInputElement>(dropdownOpen)

  // Intersection observer to detect when the chart is approaching the viewport — see
  // CHART_EMBED_LEAD_MARGIN for why it fires early rather than on contact.
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: CHART_EMBED_LEAD_MARGIN,
  })

  /**
   * Generates the final API URL for the chart based on the selected models and measurements.
   */
  const generateChartUrl = (comparisonModelId?: string, compMeasurementID?: string): void => {
    const baseRequest = new URL(`${api_url}vile/charts/${style}`)

    const params = new URLSearchParams()

    params.append('modelID', modelID)
    params.append('measureID', measureID)

    if (comparisonModelId) {
      params.append('comparisonModelID', String(comparisonModelId))
    }

    if (compMeasurementID) {
      params.append('comparisonMeasurementID', String(compMeasurementID))
    }

    if (targetID) {
      params.append('targetID', targetID)
    }
    if (nudge) {
      params.append('nudge', nudge)
    }

    baseRequest.search = params.toString()

    setChartUrl(baseRequest.toString())
  }

  /**
   * Updates the loading state of the chart.
   */
  const updateLoadingState = (state: boolean): void => {
    setIsLoading(state)
  }

  /**
   * Manages the visibility of the selection dropdown.
   */
  const updateDropdownVisibility = (visible: boolean): void => {
    setDropdownOpen(visible)
  }

  /**
   * Resets the model/measure filters and search value to their original states.
   */
  const resetFilter = (): void => {
    setFilteredModels(vileModels)
    setFilteredModelMeasures(vileModelMeasures)
    setFilterValue('')
  }

  /**
   * Handles changing the model used for comparison.
   * Fetches available measurements for the newly selected model and updates the chart URL.
   */
  const handleComparisonChange = async (newModel: VileModel): Promise<void> => {
    setScrollPosition(window.scrollY)
    setDropdownOpen(false)
    setSelectedModel({
      id: newModel.id,
      name: newModel.product.name,
    })
    setIsLoading(true)

    const { measures } = await getVileModelMeasures(api_url, newModel.id)

    if (measures.length && measures.length > 0) {
      generateChartUrl(String(newModel.id), String(measures[0].id))
    }
  }
  /**
   * Handles changing the selected measurement for the current model.
   */
  const handleModelMeasureChange = (newMeasure: VileModelMeasure): void => {
    setScrollPosition(window.scrollY)
    setDropdownOpen(false)
    setSelectedModelMeasure({
      id: newMeasure.id,
      name: newMeasure.name,
    })
    setIsLoading(true)
    generateChartUrl(String(modelID), String(newMeasure.id))
  }

  /**
   * Filters the lists of models and measurements based on the user's search input.
   */
  const filterModels = (event: ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault()
    const newFilter: string = event.target.value.toLowerCase()
    const newFilteredModels = vileModels.filter(singleModel =>
      singleModel.product.name.toLowerCase().includes(newFilter)
    )
    const newFilteredModelMeasures = vileModelMeasures.filter(singleModelMeasure =>
      singleModelMeasure.name.toLowerCase().includes(newFilter)
    )

    setFilterValue(newFilter)
    setFilteredModels(newFilteredModels)
    setFilteredModelMeasures(newFilteredModelMeasures)
  }

  // --- Side Effects ---
  useEffect(() => {
    /**
     * Fetches all available models for comparison from the API.
     */
    const fetchModels = async (): Promise<void> => {
      setIsLoading(true)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const vileModelResponse: VileModelResponse = await getVileModels(api_url)

      const currentSelectedModel = vileModelResponse.models.find(
        cursorModel => String(cursorModel.id) === defaultComparisonModelId
      )

      if (
        limitComparisonModels &&
        allowedComparisonModelsIds &&
        allowedComparisonModelsIds.length < 1
      ) {
        return
      }

      if (limitComparisonModels && defaultComparisonModelId) {
        vileModelResponse.models = vileModelResponse.models.filter(
          cursorModel =>
            allowedComparisonModelsIds?.includes(String(cursorModel.id)) ||
            String(cursorModel.id) === defaultComparisonModelId
        )
      }

      if (currentSelectedModel) {
        setSelectedModel({
          id: currentSelectedModel?.id,
          name: currentSelectedModel?.product.name,
        })
      }
      setVileModels(vileModelResponse.models)
      setFilteredModels(vileModelResponse.models)
    }

    /**
     * Closes the selection dropdown if a click is detected outside the dropdown element.
     */
    function handleClickOutside(event: MouseEvent): void {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }

    /**
     * Fetches available measurements for a specific model.
     */
    const initiateModelMeasurements = async (modelId: number): Promise<void> => {
      setIsLoading(true)
      const { measures } = await getVileModelMeasures(api_url, modelId)
      if (measures.length && measures.length > 0) {
        setSelectedModelMeasure(measures[0])
      }
      setFilteredModelMeasures(measures)
      setVileModelMeasures(measures)
    }

    document.addEventListener('click', handleClickOutside)

    // Lazy load data and generate chart URL when the component enters the viewport
    if (inView && !chartUrl) {
      void generateChartUrl(defaultComparisonModelId, defaultComparisonMeasurementId)
      if (style === 'comparison') {
        void fetchModels()
      }
      if (style === 'fr') {
        void initiateModelMeasurements(parseInt(modelID, 10))
      }
    }
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  return {
    vileModels,
    filteredModels,
    selectedModel,
    selectedModelMeasure,
    scrollPosition,
    dropdownOpen,
    chartUrl,
    filterValue,
    dropdownRef,
    dropdownFilterRef,
    isLoading,
    vileModelMeasures,
    filteredModelMeasures,
    ref,
    filterModels,
    handleComparisonChange,
    handleModelMeasureChange,
    updateLoadingState,
    resetFilter,
    updateDropdownVisibility,
  }
}
